import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Partner, PartnerAkteDaten } from '../types';
import { useRechte } from '../hooks/useRechte';
import { useAktiveModule } from '../hooks/useAktiveModule';
import { darfPreise } from '../lib/rechte';
import { downloadDateiOderFehler } from '../lib/download';
import { navigiere, kannZuAuftragNavigieren, kannZuVertragNavigieren, navigiereZuVertrag } from '../lib/navigation';
import { benutzerRollen } from '../lib/tabRollen';
import {
  BESTELLUNGEN_ZIEL, PLANTAFEL_ZIEL, Reiter, VorgangFilter, akteTitel, bestellungenErreichbar,
  kennzahlKacheln, modulAktiv, plantafelErreichbar, reiterNachTaste, sichtbareReiter,
} from '../lib/partnerAkte';
import {
  BaustellenReiter, BestellungenReiter, DokumenteReiter, EingangsrechnungenReiter, KonditionenReiter,
  RechnungenReiter, TermineReiter, VerlaufReiter, VorgaengeReiter,
} from './PartnerAkteReiter';
import BelegKarte from './BelegKarte';
import BelegAnsichtModal from './BelegAnsichtModal';
import { leseRolleAusSpeicher } from './RegieberichtForm';
import './PartnerAkte.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface PartnerAkteProps {
  partnerId: number;
  onBack: () => void;
  onBearbeiten: (partner: Partner) => void;
}

const E_RECHNUNG_LABEL: Record<string, string> = {
  KEINE: 'keine',
  ZUGFERD: 'ZUGFeRD',
  XRECHNUNG: 'XRechnung',
};

type BelegAnsichtState = { url: string; title: string } | null;

/** Partnerakte (Kunde, Lieferant oder beides): fester Kopf (Stammdaten, Schnellaktionen,
 *  Kennzahlen, „Wartet auf dich") und Reiter je Partnertyp und aktiven Modulen; die
 *  Reiter-Inhalte liegen in PartnerAkteReiter.tsx. */
const PartnerAkte: React.FC<PartnerAkteProps> = ({ partnerId, onBack, onBearbeiten }) => {
  const [akte, setAkte] = useState<PartnerAkteDaten | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reiter, setReiter] = useState<Reiter | null>(null);
  const [vorgangFilter, setVorgangFilter] = useState<VorgangFilter>('alle');
  const [kontoauszugOffen, setKontoauszugOffen] = useState(false);
  const [belegAnsicht, setBelegAnsicht] = useState<BelegAnsichtState>(null);
  const [downloadFehler, setDownloadFehler] = useState('');
  const tabRefs = useRef<Partial<Record<Reiter, HTMLButtonElement | null>>>({});

  const { rechte, rollen, geladen } = useRechte();
  const aktiveModule = useAktiveModule();
  const preiseSichtbar = geladen && darfPreise(rechte);
  // Schnellaktionen und Sprünge nur, wenn der Ziel-Tab für den Benutzer erreichbar ist:
  // Modul aktiv und Tab für seine Rollen sichtbar (bis die Rollen geladen sind, zählt die
  // Hauptrolle - wie in ManagementPage).
  const alleRollen = benutzerRollen(rollen, geladen, leseRolleAusSpeicher());
  const plantafel = plantafelErreichbar(alleRollen, aktiveModule);
  const bestellungen = bestellungenErreichbar(alleRollen, aktiveModule);
  const authToken = localStorage.getItem('auth_token') || '';

  // Reiter nach Partnertyp und aktiven Modulen; der aktive Reiter folgt, wenn er wegfällt.
  const typ = akte?.partner.typ ?? 'Kunde';
  const reiterListe = sichtbareReiter(typ, aktiveModule);
  const reiterKeys = reiterListe.map(r => r.key);
  const aktiverReiter: Reiter | null = reiter && reiterKeys.includes(reiter) ? reiter : (reiterKeys[0] ?? null);
  useEffect(() => {
    if (aktiverReiter !== reiter) setReiter(aktiverReiter);
  }, [aktiverReiter, reiter]);

  // Erstes Laden zeigt den Ladezustand; spätere Aktualisierungen (Belegkarte,
  // Notizen) tauschen die Daten still aus, damit Reiter und Filter stehen bleiben.
  const ladeAkte = useCallback(async () => {
    try {
      const res = await axios.get<PartnerAkteDaten>(`${API_URL}/api/partners/${partnerId}/akte`);
      setAkte(res.data);
      setError(null);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? err.message)
        : 'Unbekannter Fehler';
      setError(`Akte konnte nicht geladen werden: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    setLoading(true);
    ladeAkte();
  }, [ladeAkte]);

  const oeffneRechnung = (id: number, nummer: string) => {
    setBelegAnsicht({
      url: `${API_URL}/api/dokumente/ausgangsrechnungen/${id}/beleg-ansicht`,
      title: `Rechnung ${nummer}`,
    });
  };

  const dokumentHerunterladen = async (dokumentId: number, dateiname: string) => {
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/${dokumentId}/datei`, dateiname, 'Dokument',
    );
    if (fehler) setDownloadFehler(fehler);
  };

  const zurPlantafel = (tag?: string) => {
    navigiere({ ...PLANTAFEL_ZIEL, params: tag ? { datum: tag } : {} });
  };

  // WAI-ARIA Tabs: Pfeiltasten/Home/End wechseln den Reiter und den Fokus.
  const tabTaste = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!aktiverReiter) return;
    const ziel = reiterNachTaste(reiterKeys, aktiverReiter, e.key);
    if (!ziel) return;
    e.preventDefault();
    setReiter(ziel);
    tabRefs.current[ziel]?.focus();
  };

  if (loading) {
    return (
      <div className="pa-container">
        <div className="pa-loading">Akte wird geladen…</div>
      </div>
    );
  }

  if (!akte) {
    return (
      <div className="pa-container">
        <button className="pa-back-btn" onClick={onBack}>← Zurück</button>
        <div className="pa-error">{error || 'Akte nicht gefunden.'}</div>
      </div>
    );
  }

  const { partner, kennzahlen, wartet } = akte;
  const kundenseite = partner.typ !== 'Lieferant';
  const lieferantenseite = partner.typ !== 'Kunde';
  const auftragLinks = kannZuAuftragNavigieren();
  const hauptkontakt = partner.ansprechpartner.find(a => a.ist_hauptkontakt);
  const adresse = partner.adresse
    || [partner.strasse, [partner.plz, partner.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const kontakt = [partner.kontakt_email, partner.kontakt_telefon].filter(Boolean).join(' · ');
  const kacheln = kennzahlKacheln(kennzahlen, preiseSichtbar, partner.typ);

  const reiterInhalt: Record<Reiter, React.ReactNode> = {
    vorgaenge: (
      <VorgaengeReiter vorgaenge={akte.vorgaenge} filter={vorgangFilter} onFilter={setVorgangFilter}
                       auftragLinks={auftragLinks} oeffneRechnung={oeffneRechnung} />
    ),
    rechnungen: (
      <RechnungenReiter rechnungen={akte.rechnungen} kontoauszug={akte.kontoauszug} preiseSichtbar={preiseSichtbar}
                        kontoauszugOffen={kontoauszugOffen} onKontoauszugWechseln={() => setKontoauszugOffen(o => !o)}
                        oeffneRechnung={oeffneRechnung} />
    ),
    bestellungen: <BestellungenReiter bestellungen={akte.einkauf.bestellungen} preiseSichtbar={preiseSichtbar} />,
    eingangsrechnungen: (
      <EingangsrechnungenReiter eingangsrechnungen={akte.einkauf.eingangsrechnungen} preiseSichtbar={preiseSichtbar} />
    ),
    termine: (
      <TermineReiter termine={akte.termine} auftragLinks={auftragLinks}
                     plantafelErreichbar={plantafel} onZurPlantafel={zurPlantafel} />
    ),
    baustellen: <BaustellenReiter baustellen={akte.baustellen} preiseSichtbar={preiseSichtbar} />,
    konditionen: (
      <KonditionenReiter konditionen={akte.konditionen} preiseSichtbar={preiseSichtbar}
                         zeigeLieferantenArtikel={lieferantenseite}
                         zeigeVertraege={modulAktiv(aktiveModule, 'vertrag')}
                         onOpenVertrag={kannZuVertragNavigieren() ? navigiereZuVertrag : undefined} />
    ),
    dokumente: (
      <DokumenteReiter dokumente={akte.dokumente} partnerId={partnerId} authToken={authToken}
                       downloadFehler={downloadFehler} onHerunterladen={dokumentHerunterladen} />
    ),
    verlauf: <VerlaufReiter partnerId={partnerId} authToken={authToken} onChange={ladeAkte} />,
  };

  return (
    <div className="pa-container">
      {/* Kopf */}
      <div className="pa-kopf">
        <button className="pa-back-btn" onClick={onBack}>← Zurück</button>
        <div className="pa-kopf-info">
          <div className="pa-titelzeile">
            <h2 className="pa-name">{partner.name}</h2>
            <span className="pa-badge" title={akteTitel(partner.typ)}>{partner.typ}</span>
            <span className={`pa-badge${partner.aktiv === false ? ' pa-badge--inaktiv' : ' pa-badge--aktiv'}`}>
              {partner.aktiv === false ? 'inaktiv' : 'aktiv'}
            </span>
          </div>
          <dl className="pa-stammdaten">
            <div><dt>Adresse</dt><dd>{adresse || '–'}</dd></div>
            <div><dt>Kontakt</dt><dd>{kontakt || '–'}</dd></div>
            <div>
              <dt>Ansprechpartner</dt>
              <dd>
                {hauptkontakt
                  ? [hauptkontakt.name, hauptkontakt.position, hauptkontakt.telefon || hauptkontakt.email]
                      .filter(Boolean).join(' · ')
                  : '–'}
              </dd>
            </div>
            <div><dt>Zahlungsbedingungen</dt><dd>{partner.zahlungsbedingungen || '–'}</dd></div>
            {kundenseite && (
              <div>
                <dt>E-Rechnung</dt>
                <dd>{E_RECHNUNG_LABEL[partner.e_rechnung_format || 'KEINE'] || partner.e_rechnung_format}</dd>
              </div>
            )}
          </dl>
        </div>
        <div className="pa-schnellaktionen">
          {kundenseite && auftragLinks && modulAktiv(aktiveModule, 'auftrag') && (
            <>
              <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                      onClick={() => navigiere({ ansicht: 'management', tab: 'auftraege', params: {} })}>
                + Angebot
              </button>
              <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                      onClick={() => navigiere({ ansicht: 'management', tab: 'auftraege', params: {} })}>
                + Auftrag
              </button>
            </>
          )}
          {lieferantenseite && bestellungen && (
            <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                    onClick={() => navigiere({ ...BESTELLUNGEN_ZIEL, params: {} })}>
              + Bestellung
            </button>
          )}
          {plantafel && (
            <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={() => zurPlantafel()}>
              + Termin
            </button>
          )}
          <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={() => onBearbeiten(akte.partner)}>
            Stammdaten bearbeiten
          </button>
          <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                  onClick={() => navigiere({ ansicht: 'chat', tab: null, params: { partner_id: partnerId } })}>
            Im Chat fragen …
          </button>
        </div>
      </div>

      {error && <div className="pa-inline-fehler">{error}</div>}

      {/* Kennzahlen */}
      <div className="pa-kennzahlen">
        {kacheln.map(k => (
          <div key={k.label} className="pa-kachel">
            <span className="pa-kachel-label">{k.label}</span>
            <span className="pa-kachel-wert">{k.wert}</span>
            {k.sub && <span className="pa-kachel-sub">{k.sub}</span>}
          </div>
        ))}
      </div>

      {/* Wartet auf dich - „Zum Kunden" führte hier nur zu sich selbst */}
      <section className="pa-wartet">
        <h3 className="pa-wartet-titel">Wartet auf dich ({wartet.anzahl})</h3>
        {wartet.eintraege.length === 0
          ? <p className="pa-empty">Nichts wartet auf dich.</p>
          : (
            <div className="pa-wartet-liste">
              {wartet.eintraege.map(k => (
                <BelegKarte key={`${k.typ}-${k.id}`} karte={k} ausblenden={['zum_kunden']}
                            onChange={() => { ladeAkte(); }} />
              ))}
            </div>
          )}
      </section>

      {/* Reiter (WAI-ARIA Tabs: ein Tab im Tab-Zyklus, Pfeiltasten wechseln) */}
      <div className="pa-tabs" role="tablist" aria-label={`Bereiche der ${akteTitel(partner.typ)}`}>
        {reiterListe.map(r => (
          <button key={r.key} type="button" role="tab"
                  id={`pa-tab-${r.key}`} aria-controls={`pa-panel-${r.key}`}
                  aria-selected={aktiverReiter === r.key} tabIndex={aktiverReiter === r.key ? 0 : -1}
                  ref={el => { tabRefs.current[r.key] = el; }}
                  className={`pa-tab${aktiverReiter === r.key ? ' pa-tab--aktiv' : ''}`}
                  onClick={() => setReiter(r.key)} onKeyDown={tabTaste}>
            {r.label}
          </button>
        ))}
      </div>
      {aktiverReiter && (
        <div className="pa-reiter" role="tabpanel" id={`pa-panel-${aktiverReiter}`}
             aria-labelledby={`pa-tab-${aktiverReiter}`} tabIndex={0}>
          {reiterInhalt[aktiverReiter]}
        </div>
      )}

      {belegAnsicht && (
        <BelegAnsichtModal
          url={belegAnsicht.url}
          title={belegAnsicht.title}
          kontext={{}}
          onClose={() => setBelegAnsicht(null)}
        />
      )}
    </div>
  );
};

export default PartnerAkte;
